import React, { useEffect, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import UserAvatar from "@/components/UserAvatar";
import TicketDialog from "@/components/TicketDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Plus, Search, MoreVertical, Trash2, Archive, KanbanSquare, GripVertical,
  MessageSquare, Paperclip, CheckSquare, Calendar, AlertCircle, Repeat,
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#3B82F6", "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899", "#64748B"];
const PRIO_CLR = { low: "bg-slate-400", medium: "bg-blue-500", high: "bg-amber-500", urgent: "bg-red-500" };
const WEEKDAYS = [
  { code: "mon", k: "wdMon" }, { code: "tue", k: "wdTue" }, { code: "wed", k: "wdWed" },
  { code: "thu", k: "wdThu" }, { code: "fri", k: "wdFri" }, { code: "sat", k: "wdSat" }, { code: "sun", k: "wdSun" },
];
const recurScheduleLabel = (tk, t) => {
  const time = tk.recurrence_time || "08:00";
  if (tk.recurrence === "weekly") return `${(tk.recurrence_days || []).map((c) => t("wd" + c.charAt(0).toUpperCase() + c.slice(1))).join(", ") || t("weekly")} · ${time}`;
  if (tk.recurrence === "monthly") return `${t("dayOfMonth")} ${tk.recurrence_dom || 1} · ${time}`;
  return `${t("daily")} · ${time}`;
};

export default function Tickets() {
  const { t } = useT();
  const [board, setBoard] = useState({ columns: [], tickets: [], is_super_admin: false });
  const [meta, setMeta] = useState({ clients: [], users: [], is_super_admin: false, is_manager: false });
  const [filters, setFilters] = useState({ client: "all", priority: "all", q: "" });
  const [openId, setOpenId] = useState(null);
  const [colDlg, setColDlg] = useState(null);      // {id?, name, color, manager_ids}
  const [ticketDlg, setTicketDlg] = useState(null); // {column_id}
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/tickets/board").then((r) => setBoard(r.data));
  }, []);
  useEffect(() => { load(); api.get("/tickets/meta").then((r) => setMeta(r.data)); }, [load]);
  useEffect(() => { const o = params.get("open"); if (o) { setOpenId(o); params.delete("open"); setParams(params, { replace: true }); } }, []); // eslint-disable-line

  const isSuper = board.is_super_admin;
  const isAdmin = board.is_admin || board.is_super_admin;
  const cname = (id) => meta.clients.find((c) => c.id === id)?.name;

  const ticketsFor = (colId) => board.tickets
    .filter((tk) => tk.column_id === colId)
    .filter((tk) => filters.client === "all" || tk.ri_client_id === filters.client)
    .filter((tk) => filters.priority === "all" || tk.priority === filters.priority)
    .filter((tk) => !filters.q || (tk.title + tk.number).toLowerCase().includes(filters.q.toLowerCase()))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const onDragEnd = async (result) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;
    if (type === "COLUMN") {
      if (source.index === destination.index) return;
      const cols = [...board.columns];
      const [moved] = cols.splice(source.index, 1);
      cols.splice(destination.index, 0, moved);
      setBoard((b) => ({ ...b, columns: cols }));
      try { await api.put("/tickets/columns/reorder", { ordered_ids: cols.map((c) => c.id) }); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
      return;
    }
    // TICKET
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    const tickets = board.tickets.map((x) => ({ ...x }));
    const t0 = tickets.find((x) => x.id === draggableId);
    if (!t0) return;
    t0.column_id = destination.droppableId;
    t0.order = destination.index;
    // renumber destination column locally
    setBoard((b) => ({ ...b, tickets }));
    try { await api.put(`/tickets/${draggableId}/move`, { column_id: destination.droppableId, order: destination.index }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const saveColumn = async () => {
    if (!colDlg.name?.trim()) return;
    try {
      if (colDlg.id) await api.put(`/tickets/columns/${colDlg.id}`, { name: colDlg.name, color: colDlg.color, manager_ids: colDlg.manager_ids });
      else await api.post("/tickets/columns", { name: colDlg.name, color: colDlg.color, manager_ids: colDlg.manager_ids });
      setColDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archiveCol = async (c) => { await api.put(`/tickets/columns/${c.id}`, { archived: true }); load(); };
  const deleteCol = async (c) => { if (!window.confirm(`${t("deleteColumn")} "${c.name}"? (${t("tickets")})`)) return; await api.delete(`/tickets/columns/${c.id}`); load(); };

  const createTicket = async (payload) => {
    try { await api.post("/tickets", payload); setTicketDlg(null); load(); toast.success(t("createTicket")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header + filters */}
      <div className="px-4 sm:px-6 py-4 border-b border-border flex flex-wrap items-center gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><KanbanSquare className="w-6 h-6 text-primary" />{t("tickets")}</h1>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder={t("search")} className="pl-8 h-9 w-40" data-testid="ticket-search" />
          </div>
          <Select value={filters.client} onValueChange={(v) => setFilters((f) => ({ ...f, client: v }))}>
            <SelectTrigger className="h-9 w-40" data-testid="filter-client"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allClients")}</SelectItem>{meta.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v }))}>
            <SelectTrigger className="h-9 w-32" data-testid="filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("priority")}</SelectItem>
              {["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{t("priority" + p.charAt(0).toUpperCase() + p.slice(1))}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && <Button size="sm" onClick={() => setColDlg({ name: "", color: COLORS[0], manager_ids: [] })} data-testid="add-column-btn"><Plus className="w-4 h-4 mr-1" />{t("addColumn")}</Button>}
        </div>
      </div>

      {board.columns.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-3 p-8 text-center">
          <KanbanSquare className="w-10 h-10" />
          <p className="text-sm max-w-sm">{isAdmin ? t("noColumns") : t("noTicketAccess")}</p>
          {isAdmin && <Button size="sm" onClick={() => setColDlg({ name: "", color: COLORS[0], manager_ids: [] })} data-testid="add-column-empty"><Plus className="w-4 h-4 mr-1" />{t("addColumn")}</Button>}
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="board" direction="horizontal" type="COLUMN">
            {(prov) => (
              <div ref={prov.innerRef} {...prov.droppableProps} className="flex-1 overflow-x-auto scrollbar-thin flex gap-3 p-4 items-start">
                {board.columns.map((col, ci) => (
                  <Draggable draggableId={col.id} index={ci} key={col.id} isDragDisabled={!isAdmin}>
                    {(dp) => (
                      <div ref={dp.innerRef} {...dp.draggableProps} className="w-72 shrink-0 rounded-xl bg-muted/40 border border-border flex flex-col max-h-full" data-testid="ticket-column">
                        <div className="flex items-center gap-2 px-3 py-2.5" {...dp.dragHandleProps}>
                          <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
                          <p className="font-semibold text-sm flex-1 truncate">{col.name}</p>
                          <Badge variant="secondary" className="text-[10px]">{ticketsFor(col.id).length}</Badge>
                          {isAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><button className="w-6 h-6 rounded hover:bg-accent flex items-center justify-center" data-testid="column-menu"><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setColDlg({ id: col.id, name: col.name, color: col.color, manager_ids: col.manager_ids || [] })} data-testid="edit-column">{t("renameColumn")} / {t("columnColor")}</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => archiveCol(col)}><Archive className="w-4 h-4 mr-2" />{t("archiveColumn")}</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => deleteCol(col)} className="text-destructive" data-testid="delete-column"><Trash2 className="w-4 h-4 mr-2" />{t("deleteColumn")}</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                        <Droppable droppableId={col.id} type="TICKET">
                          {(tprov, snap) => (
                            <div ref={tprov.innerRef} {...tprov.droppableProps} className={`flex-1 overflow-y-auto scrollbar-thin px-2 pb-2 space-y-2 min-h-[60px] rounded-lg transition-colors ${snap.isDraggingOver ? "bg-primary/5" : ""}`}>
                              {ticketsFor(col.id).map((tk, ti) => (
                                <Draggable draggableId={tk.id} index={ti} key={tk.id}>
                                  {(td) => (
                                    <div ref={td.innerRef} {...td.draggableProps} {...td.dragHandleProps}
                                      onClick={() => setOpenId(tk.id)} data-testid="ticket-card"
                                      className={`rounded-lg bg-card border border-border p-3 cursor-pointer hover:border-primary/50 transition-colors ${tk.status === "done" ? "opacity-70" : ""}`}>
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <span className="font-mono text-[10px] text-muted-foreground">{tk.number}</span>
                                        <span className={`w-2 h-2 rounded-full ${PRIO_CLR[tk.priority]}`} title={tk.priority} />
                                        {tk.status === "done" && <Badge className="bg-emerald-500 text-[9px] ml-auto">✓</Badge>}
                                      </div>
                                      <p className="text-sm font-medium leading-snug">{tk.title}</p>
                                      {tk.labels?.length > 0 && <div className="flex flex-wrap gap-1 mt-1.5">{tk.labels.map((l, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{l}</span>)}</div>}
                                      <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground flex-wrap">
                                        {tk.ri_client_id && <span className="truncate max-w-[110px]">{cname(tk.ri_client_id)}</span>}
                                        {tk.is_recurring
                                          ? <span className="flex items-center gap-0.5 text-primary font-medium" title={recurScheduleLabel(tk, t)}><Repeat className="w-3 h-3" />{recurScheduleLabel(tk, t)}</span>
                                          : tk.due_date && <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" />{tk.due_date.slice(5)}</span>}
                                        {tk.checklist?.length > 0 && <span className="flex items-center gap-0.5"><CheckSquare className="w-3 h-3" />{tk.checklist.filter((c) => c.done).length}/{tk.checklist.length}</span>}
                                        {tk.attachments?.length > 0 && <span className="flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{tk.attachments.length}</span>}
                                        <div className="flex -space-x-1.5 ml-auto">
                                          {(tk.assigned_employee_ids || []).slice(0, 3).map((uid) => {
                                            const u = meta.users.find((x) => x.id === uid);
                                            return u ? <UserAvatar key={uid} name={u.name} src={u.profile_picture} size={20} /> : null;
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {tprov.placeholder}
                            </div>
                          )}
                        </Droppable>
                        {meta.is_manager && (
                          <button onClick={() => setTicketDlg({ column_id: col.id })} data-testid="add-ticket-btn"
                            className="m-2 mt-0 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-foreground flex items-center justify-center gap-1 transition-colors">
                            <Plus className="w-4 h-4" />{t("newTicket")}
                          </button>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {openId && <TicketDialog ticketId={openId} meta={meta} open={!!openId} canEdit={meta.is_manager || isSuper}
        onOpenChange={(o) => { if (!o) setOpenId(null); }} onChanged={load} />}

      {/* Column dialog */}
      <Dialog open={!!colDlg} onOpenChange={(o) => !o && setColDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{colDlg?.id ? t("renameColumn") : t("newColumn")}</DialogTitle><DialogDescription className="sr-only">{t("tickets")}</DialogDescription></DialogHeader>
          {colDlg && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-xs">{t("ticketTitle")}</Label><Input value={colDlg.name} onChange={(e) => setColDlg((c) => ({ ...c, name: e.target.value }))} data-testid="column-name" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("columnColor")}</Label>
                <div className="flex gap-2 flex-wrap">{COLORS.map((clr) => <button key={clr} onClick={() => setColDlg((c) => ({ ...c, color: clr }))} className={`w-7 h-7 rounded-full ${colDlg.color === clr ? "ring-2 ring-offset-2 ring-primary" : ""}`} style={{ backgroundColor: clr }} data-testid="color-swatch" />)}</div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">{t("columnManagers")}</Label>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                  {meta.users.filter((u) => u.role === "admin" || u.role === "supervisor").map((u) => {
                    const on = (colDlg.manager_ids || []).includes(u.id);
                    return <button key={u.id} onClick={() => setColDlg((c) => ({ ...c, manager_ids: on ? c.manager_ids.filter((x) => x !== u.id) : [...(c.manager_ids || []), u.id] }))}
                      className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-accent"}`} data-testid="manager-toggle">{u.name}</button>;
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setColDlg(null)}>{t("cancel")}</Button><Button onClick={saveColumn} data-testid="save-column">{t("save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create ticket dialog */}
      <CreateTicketDialog dlg={ticketDlg} onClose={() => setTicketDlg(null)} meta={meta} onCreate={createTicket} />
    </div>
  );
}

function CreateTicketDialog({ dlg, onClose, meta, onCreate }) {
  const { t } = useT();
  const [f, setF] = useState({ title: "", description: "", ri_client_id: "", priority: "medium", due_date: "", is_recurring: false, recurrence: "weekly", recurrence_days: [], recurrence_time: "08:00", recurrence_dom: 1 });
  useEffect(() => { if (dlg) setF({ title: "", description: "", ri_client_id: "", priority: "medium", due_date: "", is_recurring: false, recurrence: "weekly", recurrence_days: [], recurrence_time: "08:00", recurrence_dom: 1 }); }, [dlg]);
  if (!dlg) return null;
  const submit = () => {
    if (!f.title.trim()) { toast.error(t("ticketTitle")); return; }
    onCreate({ ...f, column_id: dlg.column_id, ri_client_id: f.ri_client_id || null });
  };
  return (
    <Dialog open={!!dlg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t("newTicket")}</DialogTitle><DialogDescription className="sr-only">{t("tickets")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("ticketTitle")}</Label><Input value={f.title} onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))} data-testid="new-ticket-title" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("description")}</Label><Textarea rows={3} value={f.description} onChange={(e) => setF((x) => ({ ...x, description: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">{t("riClient")}</Label>
              <Select value={f.ri_client_id} onValueChange={(v) => setF((x) => ({ ...x, ri_client_id: v }))}>
                <SelectTrigger data-testid="new-ticket-client"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{meta.clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">{t("priority")}</Label>
              <Select value={f.priority} onValueChange={(v) => setF((x) => ({ ...x, priority: v }))}>
                <SelectTrigger data-testid="new-ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{t("priority" + p.charAt(0).toUpperCase() + p.slice(1))}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("dueDate")}</Label><Input type="date" value={f.due_date} onChange={(e) => setF((x) => ({ ...x, due_date: e.target.value }))} /></div>
          <div className="rounded-lg border border-border p-3 space-y-2.5">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!f.is_recurring} onChange={(e) => setF((x) => ({ ...x, is_recurring: e.target.checked }))} className="w-4 h-4" data-testid="new-ticket-recurring" />
              <span className="text-sm font-medium">{t("recurring")}</span>
            </label>
            {f.is_recurring && (
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <Select value={f.recurrence} onValueChange={(v) => setF((x) => ({ ...x, recurrence: v }))}>
                    <SelectTrigger className="h-9" data-testid="new-ticket-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>{["daily", "weekly", "monthly"].map((o) => <SelectItem key={o} value={o}>{t(o)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="time" value={f.recurrence_time} onChange={(e) => setF((x) => ({ ...x, recurrence_time: e.target.value }))} data-testid="new-ticket-time" />
                </div>
                {f.recurrence === "weekly" && (
                  <div className="flex flex-wrap gap-1.5" data-testid="new-ticket-days">
                    {WEEKDAYS.map((d) => {
                      const on = f.recurrence_days.includes(d.code);
                      return <button type="button" key={d.code} onClick={() => setF((x) => ({ ...x, recurrence_days: on ? x.recurrence_days.filter((c) => c !== d.code) : [...x.recurrence_days, d.code] }))} className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-white border-primary" : "border-border hover:bg-accent"}`}>{t(d.k)}</button>;
                    })}
                  </div>
                )}
                {f.recurrence === "monthly" && (
                  <div className="flex items-center gap-2 text-sm"><span>{t("dayOfMonth")}</span><Input type="number" min="1" max="31" className="h-9 w-20" value={f.recurrence_dom} onChange={(e) => setF((x) => ({ ...x, recurrence_dom: Number(e.target.value) }))} /></div>
                )}
                <p className="text-[11px] text-muted-foreground">{t("recurringHint")}</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={submit} data-testid="create-ticket-submit">{t("createTicket")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
